/**
 * Which native shell (if any) a page is being rendered FOR — the raw signals
 * and the predicates over them.
 *
 * Pure, and deliberately NOT `'use client'`: the server has to answer these
 * questions to emit the right HTML, and the client's hydration render has to
 * answer them identically. A capability gated only on the client-side answer
 * paints the web variant first and corrects itself a frame later — which is
 * exactly what "the Apple button renders and is then pulled" is on the Android
 * shell. Both sides therefore resolve through these functions; only where the
 * inputs come from differs ({@link useIsAppUser}'s `peek*` read the URL,
 * `localStorage` and `navigator`; a server component reads `searchParams` and
 * the request's `user-agent`).
 */

/**
 * Key for the native app version — the shell's `?appVersion=` query param AND
 * the `localStorage` key it is persisted under (one identity, so one constant).
 */
export const APP_VERSION_KEY = 'appVersion';

/** Same, for the platform the shell runs on (`'ios' | 'android'`). */
export const APP_PLATFORM_KEY = 'appPlatform';

export interface AppShellSignals {
  /** `appVersion` — the ONLY "this is the app" signal the shell sends. */
  version: string | null;
  /**
   * `appPlatform` — `'ios'` or `'android'`. Absent on shell builds older than
   * the one that started sending it, which is what the UA fallback is for.
   */
  platform: string | null;
  /** The WebView's user agent, the fallback for those older shells. */
  userAgent: string | null;
}

/** Is this the native shell at all? */
export function isAppShell(signals: AppShellSignals): boolean {
  return signals.version != null;
}

/**
 * App shell AND iOS.
 *
 * Prefers the shell-reported platform and falls back to the user agent when it
 * is absent — WKWebView's UA reliably carries `iPhone`/`iPad`. Anything else
 * answers FALSE, the safe direction: callers gate iOS-only capabilities on
 * this, so a wrong `true` leaves a dead button while a wrong `false` merely
 * takes the web path.
 */
export function isIosAppShell(signals: AppShellSignals): boolean {
  if (!isAppShell(signals)) return false;
  if (signals.platform) return signals.platform.toLowerCase() === 'ios';
  return /iPhone|iPad|iPod/i.test(signals.userAgent ?? '');
}

/**
 * App shell AND Android.
 *
 * Asked POSITIVELY rather than as `!isIosAppShell`, because the two are not
 * complements: an unknown shell is neither, and the Android answer is the one
 * that must be `false` by default (it SUPPRESSES UI, so a wrong `true` would
 * hide a button from the web). `version` being required is what keeps an
 * ordinary Android browser — whose UA also says `Android` — out.
 */
export function isAndroidAppShell(signals: AppShellSignals): boolean {
  if (!isAppShell(signals)) return false;
  if (signals.platform) return signals.platform.toLowerCase() === 'android';
  return /Android/i.test(signals.userAgent ?? '');
}

/**
 * The signals as a SERVER component can see them: the shell's params off the
 * entry URL, plus the request's user agent.
 *
 * `searchParams` values are `string | string[]` (Next gives an array for a
 * repeated key), so each is normalized to its first value.
 */
export function appShellSignalsFromRequest(
  params: Record<string, string | string[] | undefined>,
  userAgent: string | null,
): AppShellSignals {
  const first = (value: string | string[] | undefined) =>
    (Array.isArray(value) ? value[0] : value) ?? null;
  return {
    version: first(params[APP_VERSION_KEY]),
    platform: first(params[APP_PLATFORM_KEY]),
    userAgent,
  };
}
